import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import {
  DatabaseTableSortOrder, FlowDownstreamTablesQueryVariables, FlowDownstreamTablesQuery, TablesDisplayGridRowDataFragment, FlowDownstreamTablesFilteredByFlowOutputFieldQueryVariables, FlowDownstreamTablesFilteredByFlowOutputFieldQuery,
  FlowDownstreamTablesFilteredByFlowOutputFieldDocument,
  FlowDownstreamTablesDocument
} from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '../../../data-catalog-shared';
import { TablesDisplayGrid } from '../sharedDisplayGrid/TablesDisplayGrid';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { getRowDataWithMaxCountsForTable } from '../table/functions/TableRowDataFunctions';
import { TablesDisplayGridRowData } from '../table/types/TablesDisplayGridRowData';

type QueryVariables = FlowDownstreamTablesQueryVariables;
type QueryResults = FlowDownstreamTablesQuery;
type QueryResultListNode = TablesDisplayGridRowDataFragment;
type GridRowData = TablesDisplayGridRowData;

type QueryVariablesWithFilter = FlowDownstreamTablesFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowDownstreamTablesFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter = TablesDisplayGridRowDataFragment;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamTablesConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamTablesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow;
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field;
}

function getRowDataForTable(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResult(resultData));
}

function getRowDataForTableForFilteredQuery(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type FlowDownstreamTablesQueryGridProps =
  GridSortProps<TablesValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowDownstreamTablesQueryGrid(props: FlowDownstreamTablesQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredTablesLineageGridMessage();

  return props.filteredFlowOutputFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={FlowDownstreamTablesFilteredByFlowOutputFieldDocument}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={FlowDownstreamTablesDocument}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForTable}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
